import { Controller } from '@hotwired/stimulus'
import { OrderAPI } from "../utils/order_api"
import { DeliveryAddress } from "../utils/delivery_address"
import { AddressAPI } from "../utils/address_api"
import { PopoverHandler } from "../utils/popover_handler"
import { CartUpdatesHandler } from "../utils/cart_updates_handler"

export default class extends Controller {
  static targets = ["quantity",
                    "decreaseButton",
                    "product",
                    "form",
                    "selectedVariant",
                    "productLabel",
                    "increaseButton",
                    "quantityTotal",
                    "popoverWrapper",
                    "variantID",
                    "price",
                    "presentationUnit"]
  connect() {
    this.delayTimer
    this.orderAPI = OrderAPI()
    this.cartUpdatesHandler = CartUpdatesHandler()
  }

  updateQuantity(e) {
    e.preventDefault()
    const lineItemId = this.productTarget.dataset.lineItemId
    const productId = this.productTarget.dataset.productId

    let currentQuantity = parseInt(this.quantityTarget.value)
    let newQuantity = currentQuantity

    this.disableVariantsDropdown()
    this.disableButtons()
    e.target.setAttribute("disabled", "disabled")

    if (e.target.innerText == "+") {
      newQuantity += 1
      gtag('event', 'add_to_cart')
    } else {
      newQuantity -= 1
    }

    if (newQuantity == parseInt(this.productTarget.dataset.totalOnHand)) {
      this.showOutOfStockPopover();
    }

    this.productTarget.classList.toggle("product-in-cart", newQuantity > 0)

    if (newQuantity < 0) { return }

    this.quantityTarget.value = newQuantity;
    this.disableShoppingBagCounter()

    clearTimeout(this.delayTimer)

    this.delayTimer = setTimeout(() => {
      if (newQuantity > 0 && e.target.innerText == "+")  { this.addtoCart(newQuantity) }
      else if (newQuantity > 0 && e.target.innerText == "-") {this.updateItem(lineItemId, newQuantity)}
      // Avoid triggering remove item if the item doesn't exist in the order
      // use case: customer click on add button and then click on remove button
      if (newQuantity == 0 && lineItemId) { this.removeItem(lineItemId) }
    }, 300)
  }

  variantSelected(e) {
    e.preventDefault()
    let lineItemId = e.target.dataset.lineItemId
    let deliveryAddress = this.productTarget.dataset.deliveryAddress

    if(typeof(lineItemId) == "undefined") { lineItemId = "" }

    this.productTarget.dataset.variantId = e.target.dataset.variantId
    this.productTarget.dataset.lineItemId = lineItemId
    this.productTarget.dataset.totalOnHand = e.target.dataset.totalOnHand
    this.priceTarget.innerText = e.target.dataset.variantPrice
    this.presentationUnitTarget.innerText = e.target.dataset.variantPresentationUnit
    this.quantityTarget.value = e.target.dataset.totalInCart
    this.variantIDTarget.value = e.target.dataset.variantId
    this.quantityTotalTarget.value = e.target.dataset.totalInCart

    this.decreaseButtonTarget.toggleAttribute("disabled", e.target.dataset.totalInCart == 0)
    this.increaseButtonTarget.toggleAttribute("disabled", (parseInt(e.target.dataset.totalOnHand) <= 0 || deliveryAddress == "missing"))
    this.productTarget.classList.toggle("product-in-cart", e.target.dataset.totalInCart > 0)
  }

  showAddressPopover(e) {
    DeliveryAddress().showPopover();
  }

  outOfStockPopover() {
    let quantity = parseInt(this.quantityTarget.value)
    let stillAvailable = quantity < this.productTarget.dataset.totalOnHand
    let userStockLocation = this.popoverWrapperTarget.dataset.userStockLocation

    if(stillAvailable || userStockLocation == "false") { return }

    this.showOutOfStockPopover()
  }

  showOutOfStockPopover() {
    PopoverHandler().showPopover(this.popoverWrapperTarget)
  }

  invalidAddressPopover() {
    let inExclusionArea = this.popoverWrapperTarget.dataset.inExclusionArea == "true"
    if(this.popoverWrapperTarget.dataset.validForDeliver == "false") {
      AddressAPI().showInvalidAddressMsg(inExclusionArea, this.popoverWrapperTarget)
    }
  }

  toggleLoading(show) {
    let progress = document.querySelector("#progress")
    progress.classList.toggle("loading", show)
  }

  removeItem(lineItemId) {
    if (this.isCartPage()) { this.toggleLoading(true) }
    this.orderAPI
      .removeItem(lineItemId)
      .then((data) => {
        if (this.isCartPage()) {
          this.cartUpdatesHandler.handle(data)
        } else {
          this.quantityTotalTarget.value = 0
          this.updateShoppingBagCounter(data)
          this.updateProductThumbnailControls(data)
        }
      })
      .catch(({ }) => {
        this.productTarget.classList.add("product-in-cart")
        this.quantityTarget.value = this.quantityTotalTarget.value
        show_flash_message("error", "We couldn't update your order. Please try again")
        window.scrollTo(0, 0)
      })
      .finally(() => {
        this.enableVariantsDropdown()
        this.enableButtons()

        if (parseInt(this.quantityTarget.value) < parseInt(this.productTarget.dataset.totalOnHand)) {
          this.increaseButtonTarget.removeAttribute("disabled")
          this.increaseButtonTarget.classList.remove('disabled')
        }

        this.enableShoppingBagCounter()
        this.toggleLoading(false)
      })
  }

  addtoCart(quantity) {
    this.quantityTotalTarget.value = quantity - parseInt(this.quantityTotalTarget.value)
    let addressInput = document.getElementById("ship-address")

    if (this.isCartPage()) { this.toggleLoading(true) }

    this.orderAPI
      .createCart(this.formTarget)
      .then((data) => {
        addressInput.dataset.orderNumber = data.order.number
        addressInput.dataset.orderGuestToken = data.order.guest_token
        if (this.isCartPage()) {
          this.cartUpdatesHandler.handle(data)
        } else {
          this.productTarget.dataset.lineItemId = data.line_item.id
          this.quantityTotalTarget.value = data.line_item.quantity
          this.updateShoppingBagCounter(data)
          this.updateProductThumbnailControls(data)
        }
      })
      .catch(({ response }) => {
        let errorMessage = "We couldn't add those products to your order. Please try again"

        if(response.data.error) { errorMessage = response.data.error }

        this.quantityTarget.value = response.data.line_item.quantity
        this.updateShoppingBagCounter(data)
        this.updateProductThumbnailControls(data)
        show_flash_message("error", errorMessage)
        window.scrollTo(0, 0)
      })
      .finally(() => {
        this.toggleLoading(false)
        this.enableVariantsDropdown()
        this.enableButtons()
        this.enableShoppingBagCounter()
      })
  }
  
  updateItem(lineItemId, quantity) {
    this.quantityTotalTarget.value = quantity - parseInt(this.quantityTotalTarget.value)

    if (this.isCartPage()) { this.toggleLoading(true) }
    
    this.orderAPI
    .updateCart(lineItemId, quantity)
    .then((data) => {
      if (this.isCartPage()) {
        this.cartUpdatesHandler.handle(data)
      } else {
        this.productTarget.dataset.lineItemId = data.line_item.id
        this.quantityTotalTarget.value = data.line_item.quantity
        this.updateShoppingBagCounter(data)
        this.updateProductThumbnailControls(data)
      }
    })
    .catch(({response}) => {
      let errorMessage = "We couldn't remove those products to your order. Please try again"

      if(response.data.error) { errorMessage = response.data.error }

      this.quantityTarget.value = response.data.line_item.quantity
      this.updateShoppingBagCounter(data)
      this.updateProductThumbnailControls(data)
      show_flash_message("error", errorMessage)
      window.scrollTo(0, 0)
    })
    .finally(() => {
      this.toggleLoading(false)
      this.enableVariantsDropdown()
      this.enableButtons()
      this.enableShoppingBagCounter()
    })
}

  updateProductThumbnailControls (data) {
    
    let productControlsWrappers = document.querySelectorAll(`[data-hook="product-controls-${data.product_id}"]`)

    if (productControlsWrappers.length > 0) {
      productControlsWrappers.forEach(el => el.innerHTML = data.updated_product_controls)
    }
  }

  enableVariantsDropdown () {
    let form = this.formTarget
    let toggleDropdown = form.querySelector(".dropdown-toggle")
    if (toggleDropdown) { toggleDropdown.classList.remove("disabled") }
  }

  disableButtons () {
    let buttons = $(".product-in-cart").find("button[type=submit]")
    buttons.addClass('disabled')
    buttons.prop("disabled", true)
  }

  disableVariantsDropdown () {
    let form = this.formTarget
    let toggleDropdown = form.querySelector(".dropdown-toggle")

    if (toggleDropdown) { toggleDropdown.classList.add("disabled") }
  }

  enableShoppingBagCounter () {
    let cartIcon = document.querySelector(".shopping-bag-wrapper")
    cartIcon.classList.remove("disabled")
  }

  enableButtons () {
     $(".product-in-cart").each(function(index, div) {
      if (parseInt(div.getAttribute('data-total-on-hand')) == parseInt(div.querySelector("input[type=text]").value)) {
        let button = $(this).find("button[type=submit]:first")
        button.removeClass('disabled')
        button.prop("disabled", false)
      } else {
        let buttons = $(this).find("button[type=submit]")
        buttons.removeClass('disabled')
        buttons.prop("disabled", false)
      }
     })
  }

  disableShoppingBagCounter () {
    let cartIcon = document.querySelector(".shopping-bag-wrapper")
    cartIcon.classList.add("disabled")
  }

  updateShoppingBagCounter (data) {
    let cartBag = document.querySelector(".shopping-bag-item-count")
    cartBag.innerText = data.order.item_count
  }

  isCartPage () {
    return window.location.pathname === "/store/cart"
  }
}
